import makeWatch from 'redux-watch';
import { sendRequest } from '../../lib-alme-request/src/lib-alme-request';
import {
    operatorTyping, removeQueueBanners,
    saveOperatorData, setQueueText,
    updateChatSessionState,
} from './actions/actions';
import {DefaultSettings, settings} from "../../../settings/settings-util";
import {Store} from "redux";
import {AlmeState} from "../../../wireup/AlmeState";
import {EntryOriginTypesV5} from "@alme/api-ts/Alme/EntryOriginTypes";
import {AlmeUIInput} from "../../../types/input";
import {OperatorData} from "./models/OperatorData";
import {processResponse} from "../../lib-alme-request/src/actions/actions";
import {AlmeUIResponse} from "../../../types/response";


export default class LiveChatState
{

    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _errorCount = 0;
    _operatorTypingTimeoutHandle: NodeJS.Timeout | null = null;


    //#region Public Interface

    constructor(settings)
    {
        this._settings = settings;
        this.resetErrorCount();
    }

    initialize(store: Store<AlmeState>): void
    {
        this._store = store;
    }

    addStatusChangeHandler(handler): void
    {
        const watch = makeWatch(this._store?.getState.bind(this), 'LiveChat.SessionState');

        this._store?.subscribe(watch((...args) => handler(...args)));
    }

    resetErrorCount(): void
    {
        this._errorCount = 0;
    }

    get sessionId(): string
    {
        return this._store?.getState().Conversation.SessionId ?? '';
    }

    updateOperatorTyping(): void
    {
        const stateLocation = `${this.loc.Base}.${this.loc.operatorTyping}`;

        if (this._operatorTypingTimeoutHandle)
        {
            window.clearTimeout(this._operatorTypingTimeoutHandle);
        }
        else
        {
            this._store?.dispatch(operatorTyping(stateLocation, true));
        }

        this._operatorTypingTimeoutHandle = setTimeout(() =>
        {
            this._store?.dispatch(operatorTyping(stateLocation, false));
            this._operatorTypingTimeoutHandle = null;
        }, this._settings.livechat.timeouts.operatorTypingDelayMS);
    }

    updateChatSessionState(newState: string | undefined): void
    {
        if (typeof newState === "undefined" || newState === this.sessionStatus) { return; }

        this._store?.dispatch(updateChatSessionState(`${this.loc.Base}.${this.loc.sessionState}`, newState));
    }

    handleErrorState(): void
    {
        const { maxErrorCountAllowed } = this._settings.livechat.error;

        if (!(this._errorCount++ > maxErrorCountAllowed)) { return; }

        this.resetErrorCount();
        this.endChat('System', 'Internal');

        console.error("Ending Live Chat Session: Hit max error threshold.");
    }

    endChat(ender: string, origin: EntryOriginTypesV5): void
    {
        const { terminateSessionEventName } = this._settings.livechat.reportingEventConstants;

        const input: AlmeUIInput =
        {
            messageType: 'ReportingEventRequest',
            eventName: terminateSessionEventName,
            eventValue: ender,
            origin,
        };

        sendRequest(input);
    }

    dispatchResponse(response: AlmeUIResponse, messageType: string): void
    {
        const stateLocation = this._settings.stateLocations.Conversation.processedResponse;

        this._store?.dispatch(processResponse(stateLocation, response, messageType));
    }

    get isOnline(): boolean
    {
        return this.hasHistory && this.isOnlineStatus(this.sessionStatus);
    }

    get sessionStatus(): string
    {
        const { Base, sessionState } = this._settings.stateLocations.LiveChat;

        return this._store?.getState()[Base][sessionState] ?? "";
    }

    get statuses(): typeof settings.livechat.sessionState
    {
        return this._settings.livechat.sessionState;
    }

    isOnlineStatus(status: string): boolean
    {
        const { queued, online } = this.statuses;

        return [queued, online].includes(status);
    }

    isQueuedStatus(status: string): boolean
    {
        return status === this._settings.livechat.sessionState.queued;
    }

    isJoining(fresh: string, old: string): boolean
    {
        const { online } = this._settings.livechat.sessionState;

        return fresh === online && old !== online;
    }

    // These two look funky, but we don't want to make
    // edges on weird statuses, e.g. LoggedOut
    isStarting(fresh: string, old: string): boolean
    {
        return this.isOnlineStatus(fresh) && !this.isOnlineStatus(old);
    }

    isEnding(fresh: string, old: string): boolean
    {
        return this.isOfflineStatus(fresh) && !this.isOfflineStatus(old);
    }

    saveOperatorData(operatorData: OperatorData): void
    {
        const stateLocation = `${this.loc.Base}.${this.loc.operatorData}`;

        this._store?.dispatch(saveOperatorData(operatorData, stateLocation));
    }

    getOperatorData(index: number): OperatorData | undefined
    {
        const operatorData = this._store?.getState()[this.loc.Base][this.loc.operatorData];

        return index < operatorData.length ? operatorData[index] : undefined;
    }

    getOperatorDataLength(): number
    {
        return this._store?.getState()[this.loc.Base][this.loc.operatorData].length ?? 0;
    }

    getLatestOperatorData(): OperatorData | undefined
    {
        const operatorData = this._store?.getState()[this.loc.Base][this.loc.operatorData];

        return operatorData.length > 0 ? operatorData[operatorData.length - 1] : undefined;
    }

    saveQueueBannerText(text: string | null): void
    {
        const stateLocation = `${this.loc.Base}.${this.loc.queueText}`;

        this._store?.dispatch(setQueueText(stateLocation, text));
        this.scrollToBanner();

    }

    scrollToBanner(): void
    {
        const conversationAreaCollection = document.querySelectorAll(`.${this._settings.classNames.conversationAreaClassName}`);
        if (conversationAreaCollection.length > 0) {
            const conversationArea = conversationAreaCollection[0];
            conversationArea.scrollTop = conversationArea.scrollHeight;
        }
    }

    getCurrentQueueText(): string | undefined
    {
        return this._store?.getState()[this.loc.Base][this.loc.queueText];
    }

    historyContainsQueueBanners(): boolean
    {
        const { livechatQueuedBanner } = this._settings.messageConstants;

        return !!this._store?.getState().History.HistoryMessages.find(historyMessage =>
            historyMessage.type === livechatQueuedBanner);
    }

    removeQueueBanners(): void
    {
        this._store?.dispatch(removeQueueBanners());
    }

    //#endregion



    //#region Helper Methods

    get hasHistory(): boolean
    {
        return !!this._store?.getState().History.HistoryMessages.length ?? false;
    }

    get loc(): typeof settings.stateLocations.LiveChat
    {
        return this._settings.stateLocations.LiveChat;
    }

    isOfflineStatus(status: string): boolean
    {
        const { offline } = this.statuses;

        return !status || (status === offline);
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/livechat-foundation/src/livechat-state.ts